import {
  Crown,
  FileText,
  BookOpen,
  Code2,
  Info,
  Sparkles,
  ShoppingBag,
} from "lucide-react";
import type {
  NavSection,
  NavSubLink,
} from "@workspace/gamja-platform/navigation";

export type {
  NavSection,
  NavSubLink,
} from "@workspace/gamja-platform/navigation";

/**
 * 전역 네비게이션 SSOT (JCG-2067).
 *
 * 7개 전역 섹션 + 각 섹션의 하위 링크를 단일 데이터로 정의한다.
 * 소비처:
 *  - `IconRail.tsx` — 아이콘 레일 호버 확장 + 플라이아웃 서브메뉴
 *  - `Menu.tsx` — 모바일 드롭다운(생존 예정 surface)
 *
 * 데스크탑 상단 NAV(NavigationMenu, hero 카드)는 추후 레일로 완전 이전되며 제거 예정이라
 * 현재는 이 SSOT 를 소비하지 않는다(전환기). 새 surface 를 추가할 땐 반드시 이 파일을 단일
 * 소스로 재사용하고 링크를 중복 정의하지 말 것.
 */

const THEORY_SUBJECT_ICONS = {
  db: "/theory/contents/theory-expected-questions/db_gamja.webp",
  networkOs: "/theory/contents/theory-expected-questions/network_gamja-1.webp",
  swDev: "/theory/contents/theory-expected-questions/sw-dev_gamja.webp",
  swDesign: "/theory/contents/theory-expected-questions/sw-design_gamja.webp",
  securityNewtech: "/theory/contents/theory-expected-questions/security_gamja.webp",
} as const;

const CODING_SUBJECT_ICONS = {
  common: "/coding/common/common_gamja.webp",
  c: "/coding/c-language/c-language_gamja.webp",
  java: "/coding/java/java_gamja.webp",
  python: "/coding/python/python_gamja.webp",
  sql: "/coding/sql/sql_gamja.webp",
} as const;

export const CODING_EXPECTED_QUESTION_LINKS: NavSubLink[] = [
  {
    href: "/coding/coding-expected-questions",
    label: "C언어",
    iconSrc: CODING_SUBJECT_ICONS.c,
  },
  {
    href: "/coding/coding-expected-questions-java",
    label: "Java",
    iconSrc: CODING_SUBJECT_ICONS.java,
  },
  {
    href: "/coding/coding-expected-questions-python",
    label: "Python",
    iconSrc: CODING_SUBJECT_ICONS.python,
  },
  {
    href: "/coding/coding-expected-questions-sql",
    label: "SQL",
    iconSrc: CODING_SUBJECT_ICONS.sql,
  },
];

/**
 * 콘텐츠·모의고사·감자 마켓·커뮤니티의 수동 UPDATE 캠페인 신호를 메뉴별로 제어한다.
 * 다시 알릴 메뉴를 true로 바꾸면 섹션 pill, 하위 항목 pill, IconRail 도트가 함께 복원된다.
 * 데스크톱 상단 메뉴의 인라인 신호도 이 상수를 공유한다.
 *
 * 2026-10-11: 섹션 캠페인은 모두 끄고 72시간의 전사 페이지 링크에만 UPDATE를 표시한다.
 */
export const NAV_UPDATE_SIGNAL_VISIBILITY = {
  pastExam: false,
  theoryStudy: false,
  codingStudy: false,
  mockExam: false,
  market: false,
  community: false,
} as const;

export const UPDATED_PAST_EXAM_REVIEW_LINK = {
  href: "/past-exam/2026/2026-2-review",
  label: "26년 2회 실기 후기",
  highlight: true,
  ...(NAV_UPDATE_SIGNAL_VISIBILITY.pastExam
    ? { badge: "UPDATE" as const }
    : {}),
} satisfies NavSubLink;

export const STUDY_PLAN_LINKS = {
  warrior: {
    href: "/theory/72-hour-study-plan",
    label: "72시간의 전사",
    highlight: true,
    badge: "UPDATE",
  },
  theory: {
    href: "/theory/4week-study-plan",
    label: "4주 이론 공부계획",
    highlight: true,
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.theoryStudy
      ? { badge: "UPDATE" as const }
      : {}),
  },
  coding: {
    href: "/coding/coding-study-plan",
    label: "6주 코딩 공부계획",
    highlight: true,
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.codingStudy
      ? { badge: "UPDATE" as const }
      : {}),
  },
} satisfies Record<string, NavSubLink>;

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "exam",
    label: "모의고사",
    icon: Crown,
    href: "/final-mock-exam",
    match: ["/final-mock-exam", "/mock-exam"],
    accent: true,
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.mockExam
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    items: [
      {
        href: "/final-mock-exam",
        label: "파이널 모의고사",
        highlight: true,
        ...(NAV_UPDATE_SIGNAL_VISIBILITY.mockExam
          ? { badge: "UPDATE" as const }
          : {}),
      },
      { href: "/mock-exam", label: "프리미엄 모의고사", highlight: true },
    ],
  },
  {
    key: "past",
    label: "실기 기출",
    icon: FileText,
    href: "/past-exam",
    match: ["/past-exam"],
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.pastExam
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    items: [
      { href: "/past-exam", label: "기출 문제 + 후기", highlight: true },
      UPDATED_PAST_EXAM_REVIEW_LINK,
      {
        href: "/past-exam/2026",
        label: "2026년 기출 문제",
        ...(NAV_UPDATE_SIGNAL_VISIBILITY.pastExam
          ? { badge: "UPDATE" as const }
          : {}),
      },
      { href: "/past-exam/2025", label: "2025년 기출 문제" },
      { href: "/past-exam/2024", label: "2024년 기출 문제" },
      { href: "/past-exam/2023", label: "2023년 기출 문제" },
      { href: "/past-exam/2022", label: "2022년 기출 문제" },
      { href: "/past-exam/2021", label: "2021년 기출 문제" },
      { href: "/past-exam/2020", label: "2020년 기출 문제" },
    ],
  },
  {
    key: "theory",
    label: "실기 이론",
    icon: BookOpen,
    href: "/theory",
    match: ["/theory"],
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.theoryStudy
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    items: [
      { href: "/theory", label: "실기 이론 공부법" },
      {
        href: "/theory/potato-theory-quiz",
        label: "실기 이론 랜덤 퀴즈",
        highlight: true,
      },
      {
        href: "/theory/potato-theory-hard-quiz",
        label: "실기 이론 매운감자퀴즈",
        highlight: true,
        iconSrc: "/main/spicy_gamja_v2.webp",
      },
      {
        href: "/theory/theory-expected-questions",
        label: "이론 압축 공부법",
        highlight: true,
        ...(NAV_UPDATE_SIGNAL_VISIBILITY.theoryStudy
          ? { badge: "UPDATE" as const }
          : {}),
      },
      {
        href: "/theory/jeongcheogi-difficulty-theory",
        label: "이론 난이도 체감",
        highlight: true,
      },
      STUDY_PLAN_LINKS.theory,
      STUDY_PLAN_LINKS.warrior,
      { href: "/theory/db", label: "DB", iconSrc: THEORY_SUBJECT_ICONS.db },
      {
        href: "/theory/network-os",
        label: "네트워크/OS",
        iconSrc: THEORY_SUBJECT_ICONS.networkOs,
      },
      {
        href: "/theory/sw-dev",
        label: "SW 개발",
        iconSrc: THEORY_SUBJECT_ICONS.swDev,
      },
      {
        href: "/theory/sw-design",
        label: "SW 설계",
        iconSrc: THEORY_SUBJECT_ICONS.swDesign,
      },
      {
        href: "/theory/security-newtech",
        label: "보안/신기술",
        iconSrc: THEORY_SUBJECT_ICONS.securityNewtech,
      },
    ],
  },
  {
    key: "coding",
    label: "실기 코딩",
    icon: Code2,
    href: "/coding",
    match: ["/coding"],
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.codingStudy
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    items: [
      { href: "/coding", label: "실기 코딩 공부법" },
      {
        href: "/coding/potato-coding-quiz",
        label: "실기 코딩 랜덤 퀴즈",
        highlight: true,
      },
      {
        href: "/coding/potato-coding-hard-quiz",
        label: "실기 코딩 매운감자 퀴즈",
        highlight: true,
        iconSrc: "/main/spicy_gamja_v2.webp",
      },
      STUDY_PLAN_LINKS.coding,
      STUDY_PLAN_LINKS.warrior,
      {
        label: "코딩 압축 공부법",
        highlight: true,
        ...(NAV_UPDATE_SIGNAL_VISIBILITY.codingStudy
          ? { badge: "UPDATE" as const }
          : {}),
        children: CODING_EXPECTED_QUESTION_LINKS,
      },
      {
        href: "/coding/jeongcheogi-difficulty-coding",
        label: "코딩 난이도 체감",
        highlight: true,
      },
      {
        href: "/coding/common",
        label: "공통 기초",
        iconSrc: CODING_SUBJECT_ICONS.common,
      },
      { href: "/coding/c-language", label: "C언어", iconSrc: CODING_SUBJECT_ICONS.c },
      { href: "/coding/java", label: "Java", iconSrc: CODING_SUBJECT_ICONS.java },
      { href: "/coding/python", label: "Python", iconSrc: CODING_SUBJECT_ICONS.python },
      { href: "/coding/sql", label: "SQL", iconSrc: CODING_SUBJECT_ICONS.sql },
    ],
  },
  {
    key: "info",
    label: "시험 정보",
    icon: Info,
    href: "/exam-registration/jcg-test-center",
    match: ["/exam-registration"],
    items: [
      { href: "/exam-registration/jcg-test-center", label: "시험장 찾기" },
      { href: "/exam-registration/jeongcheogi-pass-rate", label: "정처기 합격률 모음", highlight: true },
      { href: "/exam-registration/jeongcheogi-pre-application", label: "정처기 원서 사전 입력" },
      { href: "/exam-registration/jeongcheogi-written-exam-strategy", label: "정처기 필기 시험일 선택 전략" },
      { href: "/exam-registration/jeongcheogi-study-period", label: "정처기 준비기간" },
      { href: "/exam-registration/jeongcheogi-application", label: "정처기 원서 접수 방법" },
      { href: "/exam-registration/jeongcheogi-fee-discount", label: "정처기 응시료 50% 할인" },
      { href: "/exam-registration/jeongcheogi-eligibility", label: "응시자격 서류 제출 확인 방법" },
    ],
  },
  {
    key: "community",
    label: "커뮤니티",
    icon: Sparkles,
    href: "/community",
    match: ["/community", "/forum"],
    accent: true,
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.community
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    // 순서: 감자단 잡담 → 질문·버그 게시판 → 시험 후기 담벼락 → 문제 포럼 → 두근두근 문제 복원 (2026-09-18).
    items: [
      {
        href: "/community/talk",
        label: "감자단 잡담",
        highlight: true,
        badge: "NEW",
      },
      { href: "/community/issues", label: "질문·버그 게시판", badge: "NEW" },
      { href: "/community", label: "시험 후기 담벼락" },
      { href: "/forum/jcg", label: "문제 포럼" },
      {
        href: "/community/restoration",
        label: "두근두근 문제 복원",
        highlight: true,
      },
    ],
  },
  {
    key: "market",
    label: "감자 마켓",
    icon: ShoppingBag,
    href: "/membership",
    match: ["/membership", "/summary-pdf"],
    ...(NAV_UPDATE_SIGNAL_VISIBILITY.market
      ? { badge: "UPDATE" as const, dot: "ping" as const }
      : {}),
    items: [
      // 감자 이용권의 "곧 마감"은 판매 종료 시각 의존이라 여기 두지 않고 소비 시점에 주입한다
      // (`@/lib/nav-timed-badges` 의 withTimedNavBadge — Menu·IconRail 공용). 위 badge 주석 참고.
      { href: "/membership", label: "감자 이용권" },
      {
        href: "/summary-pdf",
        label: "요약 PDF",
        ...(NAV_UPDATE_SIGNAL_VISIBILITY.market
          ? { badge: "UPDATE" as const }
          : {}),
      },
      { href: "/summary-pdf/changelog", label: "요약본 변경 이력" },
    ],
  },
];

/** 현재 경로가 섹션 match prefix 중 하나에 해당하는지 */
export function isSectionActive(pathname: string, match: string[]): boolean {
  return match.some((m) => pathname === m || pathname.startsWith(m + "/"));
}
